import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TOTAL_ONBOARDING_STEPS } from '@/constants/onboarding';
import { RootState } from '../store';

export interface OnboardingState {
    isFirstTimeUser: boolean;
    currentStep: number;
    completedSteps: number[];
    skippedSteps: number[];
    isActive: boolean;
    completionPercentage: number;
    preferences: {
        showTips: boolean;
        autoplayTours: boolean;
        highlightMode: 'overlay' | 'subtle';
    };
}

const initialState: OnboardingState = {
    isFirstTimeUser: true,
    currentStep: 0,
    completedSteps: [],
    skippedSteps: [],
    isActive: false,
    completionPercentage: 0,
    preferences: {
        showTips: true,
        autoplayTours: false,
        highlightMode: 'overlay'
    }
};

const onboardingSlice = createSlice({
    name: 'onboarding',
    initialState,
    reducers: {
        setFirstTimeUser: (state, action: PayloadAction<boolean>) => {
            state.isFirstTimeUser = action.payload;
        },
        setCurrentStep: (state, action: PayloadAction<number>) => {
            state.currentStep = action.payload;
        },
        completeStep: (state, action: PayloadAction<number>) => {
            const stepId = action.payload;
            if (!state.completedSteps.includes(stepId)) {
                state.completedSteps.push(stepId);
                state.isFirstTimeUser = false;
                updateCompletionPercentage(state);
            }
        },
        skipStep: (state, action: PayloadAction<number>) => {
            const stepId = action.payload;
            if (!state.skippedSteps.includes(stepId)) {
                state.skippedSteps.push(stepId);
            }
        },
        toggleOnboarding: (state, action: PayloadAction<boolean>) => {
            state.isActive = action.payload;
        },
        resetOnboarding: (state) => {
            state.currentStep = 0;
            state.completedSteps = [];
            state.skippedSteps = [];
            state.isActive = false;
            state.isFirstTimeUser = true;
            state.completionPercentage = 0;
        },
        updatePreferences: (
            state,
            action: PayloadAction<Partial<OnboardingState['preferences']>>
        ) => {
            state.preferences = { ...state.preferences, ...action.payload };
        }
    }
});

function updateCompletionPercentage(state: OnboardingState) {
    state.completionPercentage = Math.round(
        (state.completedSteps.length / TOTAL_ONBOARDING_STEPS) * 100
    );
}

export const {
    setFirstTimeUser,
    setCurrentStep,
    completeStep,
    skipStep,
    toggleOnboarding,
    resetOnboarding,
    updatePreferences
} = onboardingSlice.actions;

export const selectOnboardingState = (state: RootState) => state.onboarding;
export const selectIsFirstTimeUser = (state: RootState) => state.onboarding.isFirstTimeUser;
export const selectCurrentStep = (state: RootState) => state.onboarding.currentStep;
export const selectCompletedSteps = (state: RootState) => state.onboarding.completedSteps;
export const selectSkippedSteps = (state: RootState) => state.onboarding.skippedSteps;
export const selectIsOnboardingActive = (state: RootState) => state.onboarding.isActive;
export const selectCompletionPercentage = (state: RootState) =>
    state.onboarding.completionPercentage;
export const selectOnboardingPreferences = (state: RootState) =>
    state.onboarding.preferences;

export default onboardingSlice.reducer;
